import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import {
  Button,
  IconButton,
  Item,
  Tab,
  TabContext,
  TabList,
  TabPanel,
} from "@deere/isg.component-library";
import {
  AiGenerate,
  IconAddColumn,
  IconChevronLeft,
  IconCircleClose,
  IconPanelOn,
  StatusAutonomyActive,
  StatusAutonomyOff,
  StatusAutonomyReady,
  StatusEquipmentIdle,
  StatusEquipmentWorking,
} from "@deere/icons/new";

import EquipmentList from "../../modules/EquipmentList";
import "./JobsitePanel.css";
import EquipmentPanel from "../equipmentPanel/EquipmentPanel";
import PropertiesSubpanel from "../../modules/PropertiesSubpanel";
import MissionsSubPanel from "../../modules/MissionsSubpanel";
import MissionPanel from "../missionPanel/MissionPanel";
import SessionSubPanel from "../../modules/SessionSubpanel";
import PropertiesPanel from "../PropertiesPanel/PropertiesPanel";
import AddPropertiesPanel from "../PropertiesPanel/AddPropertiesPanel";
import OrchestratorAIPanel from "../OrchestratorAIPanel/OrchestratorAIPanel";
import { hasPermission } from "../../../../permissions";
import { getAutonomyLogNavigatorState } from "../../../../modules/navigatorStateHelper";
import { AppContext } from "../../../context/AppContext";
import useApiCall from "../../../hooks/useApiCall";
import TickingTime from "../../../components/TickingTime";

const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

// Used for making Polygons
interface PolygonProps {
  points: string; // A string representing the points of the polygon, e.g., "50,150 100,50 150,150"
  fill?: string; // Optional fill color for the polygon
  stroke?: string; // Optional stroke color for the polygon
  strokeWidth?: number; // Optional stroke width for the polygon
}

const Polygon: React.FC<PolygonProps> = ({
  points,
  fill = "lightblue", // Default fill color
  stroke = "black", // Default stroke color
  strokeWidth = 1, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};

function getStringTime(unixTime: number) {
  let formattedTime = "";
  if (unixTime) {
    const elapsed = Math.floor(unixTime / 1000); // Get elapsed time in seconds
    const days = Math.floor(elapsed / (3600 * 24));
    const hours = Math.floor((elapsed % (3600 * 24)) / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    if (days > 0) {
      formattedTime = `${days} days ${hours} hr`;
    } else if (hours > 0) {
      formattedTime = `${hours} hr ${minutes} min`;
    } else if (minutes > 0) {
      formattedTime = `${minutes} min ${seconds} sec`;
    } else {
      formattedTime = `${seconds} sec`;
    }
  } else {
    // formattedTime = `0 sec`;
    // console.log("Error, unix time unknown: ", unixTime);
    formattedTime = `0 sec`;
  }
  return formattedTime;
}

function JobsitePanel({
  toggleCabControl,
  showCabControl,
  orgName,
  fullMQTT,
  userLocation,
  setUserLocation,
  isSpoofing,
  setIsSpoofing,
  originalLocation,
  setOriginalLocation,
  mapGeometryList,
  curMissionObject,
  setCurMissionObject,
  curMission,
  setCurMission,
  currentTopicString,
  hideSideBar,
  setShowAllVehicles,
  curJobsite,
  curJobsiteIcon,
  handleClose,
  changeFirstPanel,
  panelMode,
  togglePanelMode,
  onSubPanelChange,
  setGjson,
  OrgData,
  orgDataSession,
  orgDataSessionName,
  setOrgDataSessionName,
  userName,
  vehicleIconClicked,
  vehicleData,
  selectedFeatureId,
  setSelectedFeatureId,
  currJobsiteSessionIcons,
  missionPreviewActive,
  setMissionPreviewActive,
  missionPreviewPaths,
  setMissionPreviewPaths,
  missionPreviewPoints,
  setMissionPreviewPoints,
  followedVehicleId,
  setFollowedVehicleId,
  pendingNav,
  setPendingNav,
}: {
  toggleCabControl: (vehicleName?: string) => void;
  showCabControl: boolean;
  orgName: string;
  fullMQTT: any;
  curMissionObject: any;
  setCurMissionObject: (object: any) => void;
  curMission: Item | undefined;
  setCurMission: (item: Item) => void;
  currentTopicString: string;
  hideSideBar: () => void;
  curJobsite: string;
  curJobsiteIcon: React.JSX.Element | undefined;
  setShowAllVehicles: (setBoolean: boolean) => void;
  handleClose: () => void;
  changeFirstPanel: () => void;
  panelMode: "one" | "two";
  togglePanelMode: () => void;
  onSubPanelChange?: (hasSubPanel: boolean) => void;
  setGjson: (gjsonFile: any) => void;
  OrgData: any;
  userLocation: any;
  setUserLocation: (loc: any) => void;
  isSpoofing: boolean;
  setIsSpoofing: (val: boolean) => void;
  originalLocation: any;
  setOriginalLocation: (loc: any) => void;
  mapGeometryList: any;
  orgDataSession: any;
  orgDataSessionName: string;
  setOrgDataSessionName: (name: string) => void;
  userName: string;
  vehicleIconClicked: boolean;
  vehicleData: any;
  selectedFeatureId: string | null;
  setSelectedFeatureId: (id: string | null) => void;
  currJobsiteSessionIcons: { [key: string]: React.JSX.Element } | undefined;
  missionPreviewActive: boolean;
  setMissionPreviewActive: (active: boolean) => void;
  missionPreviewPaths: any[];
  setMissionPreviewPaths: (paths: any[]) => void;
  missionPreviewPoints: any[];
  setMissionPreviewPoints: (points: any[]) => void;
  followedVehicleId?: string | null;
  setFollowedVehicleId?: (id: string | null) => void;
  pendingNav?: { jobsite: string; session: string; equipmentId: string } | null;
  setPendingNav?: (nav: { jobsite: string; session: string; equipmentId: string } | null) => void;
}) {
  const { user } = React.useContext(AppContext);
  const role = user.role;
  const { execute: executeJobsite } = useApiCall({ errorAsToast: true });
  const [loadingButton, setLoadingButton] = useState<string | null>(null);
  const [jobsiteTitle, setJobsiteTitle] = useState("");
  const [value, setValue] = useState("1"); //Tab Panel being shown
  const [subPanel, setSubPanel] = useState("empty"); //which sub panel to be shown
  // Jobsite panel is hidden when a deeper subpanel is open AND we're in one-panel mode
  const isHidden = subPanel !== "empty" && panelMode === "one";
  useEffect(() => {
    onSubPanelChange?.(subPanel !== "empty");
  }, [subPanel, onSubPanelChange]);
  const [equipmentList, setEquipmentList] = useState<Array<Object>>();
  const [curEquipment, setCurEquipment] = useState<Item>(); //Current equipment to be shown
  const [curProperty, setCurProperty] = useState<any>(); //Current property to be shown
  const [curMissionItem, setCurMissionItem] = useState<Item>(); //Current mission to be shown
  const [orgData, setOrgData] = useState(OrgData);
  const [sessionsList, setSessionsList] = useState<Array<string>>();
  const [addPropertyType, setAddPropertyType] = useState<String>("");
  const [numberOfMachines, setNumberOfMachines] = useState<number>(0);
  const [jobsiteAutonomyState, setJobsiteAutonomyState] = useState<string>("Off");
  const [jobsiteAutonomyStateTimestamp, setJobsiteAutonomyStateTimestamp] = useState<number>(0);

  // This updates the list of possibleOrgSessions
  useEffect(() => {
    // sessionsList, setSessionsList
    const jobsiteData = curJobsite && orgData?.[curJobsite];

    if (!jobsiteData) {
      setSessionsList([]);
      return;
    }

    const tempSessionList = Object.keys(jobsiteData);
    setSessionsList(tempSessionList);
  }, [curJobsite, orgData, curJobsiteIcon]);

  useEffect(() => {
    if (selectedFeatureId) {
      setValue("3");
    }
  }, [selectedFeatureId]);

  // useEffect(() => {
  //     console.log("Here is the isFirst: "+isFirst);
  //     if (value != null){
  //         // change map data
  //         setShowAllVehicles(false);
  //     } else {
  //         setShowAllVehicles(true);
  //     }
  // }, [subPanel,isFirst,isHidden, value])

  useEffect(() => {
    if (orgDataSession !== undefined && Object.keys(orgDataSession).length > 0) {
      // Loop through the elements of orgDataSession, if the name contains three capital leters, call add the item to the array of equipmentList

      // Loop through the elements of orgDataSession

      type InputFormat = {
        [vehicleType: string]: {
          [vehicleModel: string]: {
            [vehicleName: string]: {
              data: object;
            };
          };
        };
      };

      type OutputFormat = {
        [vehicleIndex: string]: {
          vehicleName: string;
          vehicleType: string;
          vehicleModel: string;
          data: object;
        };
      };

      function transformVehicleData(input: InputFormat): OutputFormat {
        const output: OutputFormat = {};

        let numberOfMachinesTemp = 0;
        let currentStateTemp = "off";
        let currentStateTimeTemp = 0;

        // Iterate through each Vehicle Type
        for (const vehicleType in input) {
          const vehicleModels = input[vehicleType];

          // verify that the element is a vehicle
          const capitalLetterCount = (vehicleType.match(/[A-Z]/g) || []).length;
          if (capitalLetterCount == 3) {
            // Item is a vehicle
            // Iterate through each Vehicle Model
            for (const vehicleModel in vehicleModels) {
              const vehicleNames = vehicleModels[vehicleModel];
              // Iterate through each Vehicle Name
              for (const vehicleName in vehicleNames) {
                numberOfMachinesTemp = numberOfMachinesTemp + 1;
                const data = vehicleNames[vehicleName];
                // Construct the new format
                // let vehicleIndex = vehicleType + "/" + vehicleModel + "/" + vehicleName;
                let vehicleIndex = vehicleType + "_" + vehicleModel + "_" + vehicleName;
                output[vehicleIndex] = {
                  vehicleName: vehicleName,
                  vehicleType: vehicleType,
                  vehicleModel: vehicleModel,
                  data: data,
                };

                let firstAutonomyTime = 0;
                firstAutonomyTime = (data as any)?.autonomyLog?.[
                  Object.keys((data as any)?.autonomyLog).length - 2
                ]?.timestamp;

                // Updating the jobsite autonomy state
                let thisAutonomyState = (data as any)?.autonomyLog?.[
                  Object.keys((data as any)?.autonomyLog).length - 1
                ];
                // console.log("thisAutonomyState:", thisAutonomyState);
                // console.log("thisAutonomyState?.[0]:", thisAutonomyState?.["0"]);
                // console.log("thisAutonomyState?.[state]:", thisAutonomyState?.["state"]);
                // console.log("boolean:", (thisAutonomyState?.["0"] !== "2"));
                let thisAutonomyTime = Math.floor(Date.now() - thisAutonomyState?.timestamp); // Get elapsed time in seconds
                // console.log("thisAutonomyTime: ",thisAutonomyTime);
                const navState = getAutonomyLogNavigatorState(thisAutonomyState);
                if (
                  (thisAutonomyState?.["0"] !== "1" &&
                    thisAutonomyState?.["0"] !== "2" &&
                    thisAutonomyState?.["0"] !== undefined) ||
                  (thisAutonomyState?.["0"] == undefined && navState == undefined)
                ) {
                  // Autonomy state is off
                  if (currentStateTemp == "off") {
                    currentStateTemp = "off";
                    if (thisAutonomyTime > currentStateTimeTemp) {
                      currentStateTimeTemp = thisAutonomyTime;
                    }
                  }
                } else if (thisAutonomyState?.["0"] == "1" || navState == "READY") {
                  // Autonomy state is ready
                  if (currentStateTemp == "off") {
                    currentStateTemp = "ready";
                    currentStateTimeTemp = thisAutonomyTime;
                  } else if (currentStateTemp == "ready") {
                    currentStateTemp = "ready";
                    if (thisAutonomyTime > currentStateTimeTemp) {
                      currentStateTimeTemp = thisAutonomyTime;
                    }
                  }
                } else if (navState == "INTERRUPTED") {
                  // Autonomy state is paused
                  if (currentStateTemp == "off" || currentStateTemp == "ready") {
                    currentStateTemp = "paused";
                    currentStateTimeTemp = thisAutonomyTime;
                  } else if (currentStateTemp == "paused") {
                    currentStateTemp = "paused";
                    if (thisAutonomyTime > currentStateTimeTemp) {
                      currentStateTimeTemp = thisAutonomyTime;
                    }
                  }
                } else {
                  // Autonomy state is working
                  if (currentStateTemp !== "working") {
                    currentStateTemp = "working";
                    currentStateTimeTemp = thisAutonomyTime;
                  } else if (currentStateTemp == "working") {
                    currentStateTemp = "working";
                    if (thisAutonomyTime > currentStateTimeTemp) {
                      currentStateTimeTemp = thisAutonomyTime;
                    }
                  }
                }
              }
            }
          }
        }

        // console.log("currentStateTemp: ",currentStateTemp);

        setNumberOfMachines(numberOfMachinesTemp);
        setJobsiteAutonomyState(currentStateTemp);
        setJobsiteAutonomyStateTimestamp(
          currentStateTimeTemp > 0 ? Date.now() - currentStateTimeTemp : 0,
        );
        // console.log("currentStateTimeTemp", currentStateTimeTemp);
        // console.log("getStringTime(currentStateTimeTemp)", getStringTime(currentStateTimeTemp));

        return output;
      }

      const newEquipmentList: any = transformVehicleData(orgDataSession) as any; // Temporary array to hold matching equipment

      if (newEquipmentList !== undefined) {
        // setEquipmentList(newEquipmentList); // Update the state with the new equipment list
        setEquipmentList(newEquipmentList); // Update the state with the new equipment list
        // console.log(equipmentList);
      }
    }
  }, [curJobsite, orgDataSession]);

  useEffect(() => {
    setOrgData(OrgData);
  }, [OrgData]);

  const handleCurEquipment = useCallback(
    (item: Item, i: number) => {
      //changes current equipment from equipment list callback
      setCurEquipment(item);
      setSubPanel("equipment");
    },
    [curJobsite, subPanel],
  );

  // Consume pendingNav: once equipmentList is populated and the requested machine exists,
  // open EquipmentPanel for it and clear the marker so this effect is idempotent.
  useEffect(() => {
    if (!pendingNav || !setPendingNav) return;
    if (!equipmentList) return;
    const entry = (equipmentList as any)[pendingNav.equipmentId];
    if (!entry) return;
    handleCurEquipment({ id: pendingNav.equipmentId } as Item, 0);
    setPendingNav(null);
  }, [pendingNav, equipmentList, setPendingNav, handleCurEquipment]);

  const handleCurMission = useCallback(
    (item: Item, i: number) => {
      //changes current equipment from equipment list callback
      setCurMission(item);
      setSubPanel("mission");
    },
    [curJobsite, subPanel],
  );

  const handleCurProperty = useCallback(
    (item: Item, i: number) => {
      setSubPanel("property");
    },
    [curJobsite, subPanel],
  );

  function handleAddProperty(inputAddPropertyTpe: String) {
    setSubPanel("addProperty");
    setAddPropertyType(inputAddPropertyTpe);
  }

  function handleOrchestratorAISelect() {
    setSubPanel("orchestratorAI");
  }

  const handleChange = (event: any, newValue: React.SetStateAction<string>): void => {
    //changes tab and show corresponding sub panel
    setValue(newValue);
    if (newValue == "1" && subPanel != "equipment") {
      setSubPanel("empty");
    } else if (newValue == "2" && subPanel != "mission") {
      setSubPanel("empty");
    } else if (newValue == "3" && subPanel != "properties") {
      setSubPanel("empty");
    } else if (newValue == "4") {
      setSubPanel("empty");
    }
    if (newValue != "3") {
      setSelectedFeatureId(null);
    }
  };

  const changeCurrent = useCallback(() => {
    togglePanelMode();
  }, [togglePanelMode]);

  const handleCurrentClose = useCallback(() => {
    setSubPanel("empty");
    setCurEquipment(undefined);
    setCurMission(undefined as any);
    setCurMissionObject({});
    setSelectedFeatureId(null);
  }, [subPanel, setCurMission, setCurMissionObject]);

  const navigateToMissions = useCallback(() => {
    setSubPanel("empty");
    setValue("2");
    setSelectedFeatureId(null);
  }, []);

  const navigateToAttributes = useCallback(() => {
    setSubPanel("empty");
    setValue("3");
    setSelectedFeatureId(null);
  }, []);

  useLayoutEffect(() => {
    setJobsiteTitle(curJobsite);
  }, [curJobsite, jobsiteTitle]);

  function handleBack() {
    setSelectedFeatureId(null);
    handleClose();
    setShowAllVehicles(true);
  }

  function handleShowHideList() {
    togglePanelMode();
  }

  async function sendPauseJobsite() {
    return fetch("https://" + backendURI + "/Save/PauseJobsite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ currentTopicString: currentTopicString }),
    });
  }

  async function sendResumeJobsite() {
    return fetch("https://" + backendURI + "/Save/ResumeJobsite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ currentTopicString: currentTopicString }),
    });
  }

  return (
    <>
      {!isHidden && (
        <div className="JobsitePanel">
          <div className="TopPanel">
            <div className="TopTitleBar">
              <div className="TitleBarLeft">
                <IconButton
                  icon={
                    <span>
                      <IconChevronLeft size={20} />
                    </span>
                  }
                  variant="borderless"
                  onClick={handleBack}
                  style={{ width: "32px", height: "32px", alignSelf: "center" }}
                />
                <div className="JobsiteTag">
                  {/* <Polygon points="10,10 190,10 140,190 0,190" fill="#FF00FF4D" stroke="#FF00FF" strokeWidth={2} /> */}
                  {curJobsiteIcon}
                  <div className="JobsiteText">
                    <b>{jobsiteTitle}</b>
                    <div>{numberOfMachines.toString()} Machines on Jobsite</div>
                    {jobsiteAutonomyState == "off" && (
                      <div
                        style={{
                          color: "#000000",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <StatusAutonomyOff size={20} sx={{ marginRight: "4px" }} />
                        {/* {"Off "} */}
                        <TickingTime timestamp={jobsiteAutonomyStateTimestamp} />
                      </div>
                    )}
                    {jobsiteAutonomyState == "ready" && (
                      <div
                        style={{
                          color: "#00B600",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <StatusAutonomyReady size={20} />
                        {/* {"Ready "} */}
                        <TickingTime timestamp={jobsiteAutonomyStateTimestamp} />
                      </div>
                    )}
                    {jobsiteAutonomyState == "paused" && (
                      <div
                        style={{
                          color: "rgb(255, 120, 0)",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                        <StatusEquipmentIdle size={20} sx={{ marginRight: "4px" }} />
                        {/* {"Paused "} */}
                        <TickingTime timestamp={jobsiteAutonomyStateTimestamp} />
                      </div>
                    )}
                    {jobsiteAutonomyState == "working" && (
                      <div
                        style={{
                          color: "rgb(6, 138, 252)",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                        <StatusEquipmentWorking size={20} sx={{ marginRight: "4px" }} />
                        {/* {"6 min 15 sec"} */}
                        {/* {"Working "} */}
                        <TickingTime timestamp={jobsiteAutonomyStateTimestamp} />
                      </div>
                    )}

                    {/* <div>
                                        ##.# ac
                                    </div> */}
                    {/* <div>
                                        Last Worked: 16 minutes ago
                                    </div> */}
                  </div>
                </div>
              </div>
              <div className="exitButtons">
                <IconButton
                  style={{ pointerEvents: "auto" }}
                  icon={
                    <span>
                      {panelMode === "one" ? <IconAddColumn size={20} /> : <IconPanelOn size={20} />}
                    </span>
                  }
                  variant="borderless"
                  onClick={handleShowHideList}
                />
                <IconButton
                  icon={
                    <span>
                      <IconCircleClose size={20} />
                    </span>
                  }
                  variant="borderless"
                  onClick={handleBack}
                />
              </div>
            </div>
            <div className="ButtonPanel">
              {/* sendPauseJobsite */}
              {hasPermission(role, "RESUME_MISSION") && (
                <Button
                  startIcon={<StatusEquipmentWorking size={20} />}
                  loading={loadingButton === "resume"}
                  disabled={loadingButton !== null}
                  onClick={async () => {
                    setLoadingButton("resume");
                    await executeJobsite(() => sendResumeJobsite(), {
                      successMessage: "Jobsite resumed",
                    });
                    setLoadingButton(null);
                  }}
                  style={{ marginLeft: "6px", marginRight: "6px" }}
                >
                  Resume
                </Button>
              )}
              {hasPermission(role, "PAUSE_MISSION") && (
                <Button
                  startIcon={<StatusEquipmentIdle size={20} />}
                  loading={loadingButton === "pause"}
                  disabled={loadingButton !== null}
                  onClick={async () => {
                    setLoadingButton("pause");
                    await executeJobsite(() => sendPauseJobsite(), {
                      successMessage: "Jobsite paused",
                    });
                    setLoadingButton(null);
                  }}
                  style={{ marginLeft: "6px", marginRight: "6px" }}
                >
                  Pause
                </Button>
              )}

              {/* <Button startIcon={
                            <IconArchive icon={{ style: { width: "18px", height: "18px" } }} />
                        }
                            style={{ marginLeft: "6px", marginRight: "6px" }}>
                            Archive
                        </Button> */}
              {/* <Button
                            startIcon={
                                <IconWdt icon={{ style: { width: "20px", height: "20px" } }} />
                            }
                            style={{ marginLeft: "6px", marginRight: "6px" }}>
                            Export
                        </Button> */}
              {/* <Button
                            startIcon={
                                <IconDirections icon={{ style: { width: "20px", height: "20px" } }} />
                            }
                            style={{ marginLeft: "6px", marginRight: "6px" }}
                        >
                            Directions
                        </Button> */}
              <Button
                startIcon={<AiGenerate size={20} />}
                style={{ marginLeft: "6px", marginRight: "6px" }}
                // variant="primary"
                className="SaveMissionButton"
                onClick={handleOrchestratorAISelect}
                disabled={false}
              >
                Orchestrator AI
              </Button>
            </div>
          </div>
          <TabContext value={value}>
            <TabList onChange={handleChange}>
              <Tab label="Equipment" value="1" />
              <Tab label="Missions" value="2" />
              <Tab label="Attributes" value="3" />
              <Tab label="Sessions" value="4" />
            </TabList>
            <TabPanel value="1" sx={{ padding: "16px 0 0 0" }}>
              <EquipmentList
                userLocation={userLocation}
                equipmentList={equipmentList}
                setCurrent={handleCurEquipment}
                vehicleIconClicked={vehicleIconClicked}
                vehicleData={vehicleData}
                subPanel={subPanel}
                showCabControl={showCabControl}
              />
            </TabPanel>
            <TabPanel value="2" sx={{ padding: "16px 0 0 0" }}>
              <MissionsSubPanel
                orgDataSession={orgDataSession}
                currentTopicString={currentTopicString}
                curMissionObject={curMissionObject}
                setCurMissionObject={setCurMissionObject}
                setCurMission={setCurMission}
                orgData={orgData}
                setCurrent={handleCurMission}
                subPanel={subPanel}
              />
            </TabPanel>
            <TabPanel value="3" sx={{ padding: "16px 0 0 0" }}>
              <PropertiesSubpanel
                setCurProperty={setCurProperty}
                mapGeometryList={mapGeometryList}
                currentTopicString={currentTopicString}
                setGjson={setGjson}
                orgData={orgData}
                curJobsite={curJobsite}
                orgDataSession={orgDataSession}
                orgDataSessionName={orgDataSessionName}
                setOrgDataSessionName={setOrgDataSessionName}
                setCurrent={handleCurProperty}
                addProperty={handleAddProperty}
                selectedFeatureId={selectedFeatureId}
              />
            </TabPanel>
            <TabPanel value="4" sx={{ padding: "16px 0 0 0" }}>
              <SessionSubPanel
                orgName={orgName}
                curJobsite={curJobsite}
                sessionsList={sessionsList}
                setOrgDataSessionName={setOrgDataSessionName}
                orgDataSessionName={orgDataSessionName}
                setCurrent={setOrgDataSessionName}
                currJobsiteSessionIcons={currJobsiteSessionIcons}
              />
            </TabPanel>
          </TabContext>
        </div>
      )}
      {}
      {subPanel === "mission" && (
        <MissionPanel
          orgDataSession={orgDataSession}
          currentTopicString={currentTopicString}
          curMissionObject={curMissionObject}
          setCurMissionObject={setCurMissionObject}
          orgData={orgData}
          hideSideBar={hideSideBar}
          curMission={curMission}
          handleClose={handleCurrentClose}
          changeFirstPanel={changeCurrent}
          panelMode={panelMode}
          togglePanelMode={togglePanelMode}
          missionPreviewActive={missionPreviewActive}
          setMissionPreviewActive={setMissionPreviewActive}
          missionPreviewPaths={missionPreviewPaths}
          setMissionPreviewPaths={setMissionPreviewPaths}
          missionPreviewPoints={missionPreviewPoints}
          setMissionPreviewPoints={setMissionPreviewPoints}
        />
      )}
      {subPanel === "equipment" && (
        <EquipmentPanel
          followedVehicleId={followedVehicleId}
          setFollowedVehicleId={setFollowedVehicleId}
          toggleCabControl={toggleCabControl}
          showCabControl={showCabControl}
          orgDataSession={orgDataSession}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
          isSpoofing={isSpoofing}
          setIsSpoofing={setIsSpoofing}
          originalLocation={originalLocation}
          setOriginalLocation={setOriginalLocation}
          orgData={orgData}
          currentTopicString={currentTopicString}
          hideSideBar={hideSideBar}
          curEquipment={curEquipment}
          handleClose={handleCurrentClose}
          changeFirstPanel={changeCurrent}
          panelMode={panelMode}
          togglePanelMode={togglePanelMode}
          onNavigateToMissions={navigateToMissions}
          onNavigateToAttributes={navigateToAttributes}
          missionPreviewActive={missionPreviewActive}
          setMissionPreviewActive={setMissionPreviewActive}
          missionPreviewPaths={missionPreviewPaths}
          setMissionPreviewPaths={setMissionPreviewPaths}
          missionPreviewPoints={missionPreviewPoints}
          setMissionPreviewPoints={setMissionPreviewPoints}
          orgName={orgName}
        />
      )}
      {subPanel === "property" && (
        <PropertiesPanel
          currentTopicString={currentTopicString}
          selectedProperty={curProperty}
          setSelectedProperty={() => {}}
          handleClose={handleCurrentClose}
          changeFirstPanel={changeCurrent}
          selectedFeatureId={selectedFeatureId}
          setSelectedFeatureId={setSelectedFeatureId}
        />
      )}
      {subPanel === "addProperty" && (
        <AddPropertiesPanel
          currentTopicString={currentTopicString}
          propertyType={addPropertyType}
          selectedProperty={{ name: "Property Name", type: "Property Type" }}
          setSelectedProperty={() => {}}
          handleClose={handleCurrentClose}
          changeFirstPanel={changeCurrent}
        />
      )}
      {subPanel === "orchestratorAI" && (
        <OrchestratorAIPanel
          fullMQTT={fullMQTT}
          orgDataSession={orgDataSession}
          currentTopicString={currentTopicString}
          propertyType={addPropertyType}
          selectedProperty={{ name: "Property Name", type: "Property Type" }}
          setSelectedProperty={() => {}}
          handleClose={handleCurrentClose}
          changeFirstPanel={changeCurrent}
        />
      )}
    </>
  );
}

export default JobsitePanel;
